import React, { useEffect, useState, useContext } from "react";
import { Container, Row, Col, Card, Button, Alert, Spinner, Modal } from "react-bootstrap";
import { AuthContext } from "../context/AuthContext";
import { Link } from "react-router-dom";

/**
 * This version fetches image blobs with the Authorization header
 * and renders them via object URLs, so the backend route can keep @jwt_required().
 */
export default function ViewTitleImagesPage() {
  const { token } = useContext(AuthContext);
  const [items, setItems] = useState([]);          // [{ filename, url, href }] where href is objectURL
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [previewHref, setPreviewHref] = useState(null);

  const API = "/api";

  // clean up object URLs on unmount
  useEffect(() => {
    return () => {
      items.forEach(it => it.href && URL.revokeObjectURL(it.href));
    };
  }, [items]);

  const fetchWithAuthBlob = async (url) => {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`image fetch failed ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const load = async () => {
    setBusy(true); setErr("");
    try {
      // 1) list
      const listRes = await fetch(`${API}/title-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const listData = await listRes.json().catch(() => []);
      if (!listRes.ok) throw new Error(listData.error || `Failed to load (${listRes.status})`);
      const rows = (Array.isArray(listData) ? listData : (listData.items || []))
	.filter(x => typeof x.filename === "string" && x.filename.toLowerCase().endsWith(".jpg"))

      // 2) fetch blobs in parallel (limit concurrency lightly)
      const concurrency = 4;
      let idx = 0;
      const out = new Array(rows.length);
      async function worker() {
        while (idx < rows.length) {
          const i = idx++;
          const row = rows[i];
          try {
            const href = await fetchWithAuthBlob(row.url);
            out[i] = { ...row, href };
          } catch (e) {
            out[i] = { ...row, href: null, _err: e.message };
          }
        }
      }
      await Promise.all(Array.from({ length: concurrency }, () => worker()));
      setItems(out);
    } catch (e) {
      setErr(e.message);
      setItems([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <Container className="py-3">
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="mb-0">Title Images (Step 1 Verification)</h4>
          <small className="text-muted">JPG previews from /output/titles</small>
        </Col>
        <Col className="text-end">
          <Button as={Link} to="/titleagent" variant="outline-primary" size="sm">
            ← Back to Upload
          </Button>
        </Col>
      </Row>

      <div className="d-flex gap-2 mb-3">
        <Button onClick={load} disabled={busy}>
          {busy ? <Spinner animation="border" size="sm" className="me-2" /> : null}
          Refresh
        </Button>
      </div>

      {err && <Alert variant="warning">{err}</Alert>}

      <Row className="g-3">
        {items.map((it, idx) => (
          <Col key={idx} xs={12} md={6}>
            <Card
              className="shadow-sm h-100"
              role="button"
              onClick={() => it.href && setPreviewHref(it.href)}
            >
              {it.href ? (
                <Card.Img
                  variant="top"
                  src={it.href}
                  alt={it.filename}
                  style={{ objectFit: "cover", height: 260 }}
                />
              ) : (
                <div className="ratio ratio-4x3 bg-light d-flex align-items-center justify-content-center">
                  <span className="text-muted">Preview unavailable</span>
                </div>
              )}
              <Card.Body className="py-2">
                <div className="small text-truncate" title={it.filename}>{it.filename}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
        {!busy && items.length === 0 && (
          <Col xs={12}>
            <Alert variant="light" className="text-muted border">No JPG images found in output/titles.</Alert>
          </Col>
        )}
      </Row>

      <Modal show={!!previewHref} onHide={() => setPreviewHref(null)} size="xl" centered>
        <Modal.Header closeButton><Modal.Title>Preview</Modal.Title></Modal.Header>
        <Modal.Body className="text-center">
          {previewHref && <img src={previewHref} alt="title" style={{ maxWidth: "100%", height: "auto" }} />}
        </Modal.Body>
      </Modal>
    </Container>
  );
}

